import { Feather } from '@expo/vector-icons';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalProps,
  Platform,
  PlatformIOSStatic,
  useWindowDimensions
} from 'react-native';
import styled from 'styled-components/native';
import * as Yup from 'yup';
import ContentView from '../../../components/ContentView';
import Form from '../../../components/form/Form';
import FormButton from '../../../components/form/FormButton';
import FormInput from '../../../components/form/FormInput';
import IconButton from '../../../components/IconButton';
import Spacer from '../../../components/Spacer';
import { H3, H5 } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import {
  PasswordRecoverRequest,
  usePasswordRecover
} from '../../../hooks/user/usePasswordRecover';
import ResetPasswordSentModal from './ResetPasswordSentModal';
import { log } from '../../../tools/LogHelper';
import { stringify } from 'flatted';

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
`;

const StyledDialog = styled.View<{ isMobile: boolean }>`
  width: ${p => (p.isMobile ? 100 : 90)}%;
  height: ${p => (p.isMobile ? 100 : 90)}%;
  border-radius: 10px;
  max-width: 1080px;
  max-height: ${p => (p.isMobile ? '100%' : '627px')};
  overflow: hidden;
  background-color: ${Colors.background};
`;

const StyledContentView = styled.KeyboardAvoidingView`
  flex: 1;
`;

const StyledImageBackground = styled.Image<{ isDesktop: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${p => (p.isDesktop ? '100%' : '130%')};
  height: 100%;
  max-height: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'ios' ? 48 : 20}px;
  left: 20px;
  z-index: 1;
`;

const StyledScrollView = styled.ScrollView`
  height: auto;
`;

const StyledContainerView = styled.View<{ isDesktop: boolean }>`
  flex-direction: ${p => (p.isDesktop ? 'row' : 'column')};
`;

const StyledContainerColumn = styled.View<{ isDesktop: boolean }>`
  flex: ${p => (p.isDesktop ? 1 : '0 1 auto')};
  align-items: center;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  padding: ${p =>
    p.isDesktop
      ? '124px 0 0 0'
      : `${Platform.OS === 'ios' ? 110 : 90}px 24px 0 24px`};
  max-width: ${p => (p.isDesktop ? 320 : 430)}px;
  width: 100%;
`;

const StyledFormView = styled.View<{ isDesktop: boolean }>`
  padding-top: ${p => (p.isDesktop ? 168 : 0)}px;
  max-width: 431px;
  width: 100%;
`;

export type ForgotPasswordModalProps = ModalProps;

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  ...props
}) => {
  const { onRequestClose } = props;
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const isMobile = window.width < 468;
  const formikRef = useRef<FormikProps<any> | null>();
  const platformIOS = Platform as PlatformIOSStatic;
  const backgroundAsset = isMobile
    ? require('../../../assets/intro/intro-background-start.png')
    : require('../../../assets/intro/intro-background-whole.png');

  const { success, isLoading, doPasswordRecover } = usePasswordRecover();

  const handleSubmit = (form: PasswordRecoverRequest) => {
    doPasswordRecover(form.email);
  };

  const [showEmailSent, setShowEmailSent] = useState(false);

  useEffect(() => {
    if (!isLoading && success) {
      formikRef.current?.resetForm();
      setShowEmailSent(true);
    }
  }, [success, isLoading]);

  formikRef.current?.setSubmitting(isLoading);
  return (
    <Modal animationType='fade' transparent {...props}>
      <StyledDimContainer>
        <StyledDialog isMobile={isMobile}>
          <StyledImageBackground
            isDesktop={isDesktop}
            resizeMode='cover'
            source={backgroundAsset}
          />
          <StyledContentView
            keyboardVerticalOffset={Platform.select({
              ios: platformIOS.isPad
                ? window.height >= 1024 && window.height <= 1194
                  ? window.height * 0.23
                  : window.height * 0.27
                : 0
            })}
            behavior={Platform.select({ ios: 'padding' })}
          >
            <StyledScrollView
              contentContainerStyle={{ flexGrow: 1 }}
              scrollIndicatorInsets={{ right: 1 }}
            >
              <StyledIconButton onPress={event => onRequestClose?.(event)}>
                <Feather
                  name='arrow-left'
                  size={28}
                  color={Colors.primaryPurple}
                />
              </StyledIconButton>
              <StyledContainerView isDesktop={isDesktop}>
                <StyledContainerColumn isDesktop={isDesktop}>
                  <StyledHeaderView isDesktop={isDesktop}>
                    <H3 color='medium' padding={2}>
                      Forgot Password
                    </H3>
                    <H5>
                      Enter the email associated with your account and we’ll
                      send you a link to reset your password.
                    </H5>
                  </StyledHeaderView>
                </StyledContainerColumn>
                <StyledContainerColumn isDesktop={isDesktop}>
                  <StyledFormView isDesktop={isDesktop}>
                    <Formik
                      innerRef={formik => (formikRef.current = formik)}
                      initialValues={{
                        email: ''
                      }}
                      validationSchema={Yup.object().shape({
                        email: Yup.string()
                          .email()
                          .required('Email is required.')
                      })}
                      onSubmit={formValues => {
                        handleSubmit(formValues);
                        log('onSubmit', stringify(formValues));
                      }}
                    >
                      <Form>
                        <ContentView>
                          <FormInput
                            keyboardType='email-address'
                            returnKeyType='done'
                            name='email'
                            placeholder='Email address'
                            autoCapitalize='none'
                            autoCorrect={false}
                            errorTextColor='dark'
                          />

                          <Spacer size='large' />
                          <FormButton
                            style={{ width: 236, alignSelf: 'center' }}
                          >
                            Continue
                          </FormButton>
                          <Spacer size={4} />
                        </ContentView>
                      </Form>
                    </Formik>
                  </StyledFormView>
                </StyledContainerColumn>
              </StyledContainerView>
            </StyledScrollView>
          </StyledContentView>
        </StyledDialog>
      </StyledDimContainer>
      {showEmailSent && (
        <ResetPasswordSentModal
          email={formikRef.current?.values?.email}
          visible
          onRequestClose={() => setShowEmailSent(false)}
          onBackToLogin={() => {
            setShowEmailSent(false);
            onRequestClose?.({} as any);
          }}
        />
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
